import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import PlacementForm from '../../components/PlacementForm';
import { OrgBadge, teamName } from '../../components/PlacementsTable';
import { fmtDate, fmtMoney, fmtNumber, usePaged } from '../../hooks';
import { Purpose, Alert, Badge, Card, Chip, Select, SearchBox, Skeleton, Stat } from '../../ui/basics';
import { DataTable } from '../../ui/DataTable';

const KINDS = [
  { key: 'overdue', label: 'Overdue', sub: 'projected end date has passed' },
  { key: 'expiring', label: 'Ending soon', sub: 'projected to end in the window' },
  { key: 'missing', label: 'No projected end', sub: 'no end date to plan around' },
];

export default function Watchlist({ org, lead }) {
  const { user, hasPage } = useAuth();
  const [kind, setKind] = useState('overdue');
  const [days, setDays] = useState(30);
  const [edit, setEdit] = useState(null);
  const paged = usePaged('analytics/watchlist/', { fixed: { organization: org, lead, kind, days }, initialOrdering: 'projected_end_date' });
  const { data, filters, setFilter, reload } = paged;
  const summary = data && data.summary;
  const canEdit = user.can_edit && hasPage('placements');

  const columns = [
    { key: 'candidate_name', label: 'Candidate', sortable: true, render: (r) => <b>{r.candidate_name}</b> },
    ...(org === 'combined' ? [{ key: 'organization', label: 'Organization', render: (r) => <OrgBadge name={r.organization} /> }] : []),
    { key: 'client', label: 'Client', sortable: true },
    { key: 'recruiter', label: 'Recruiter', sortable: true },
    { key: 'lead', label: 'Team', sortable: true, render: (r) => teamName(r.lead) },
    { key: 'date_of_joining', label: 'Started', sortable: true, render: (r) => fmtDate(r.date_of_joining) },
    ...(kind !== 'missing' ? [
      { key: 'projected_end_date', label: 'Projected end', sortable: true, render: (r) => fmtDate(r.projected_end_date) },
      { key: 'days_to_end', label: kind === 'overdue' ? 'Overdue by' : 'Days left', align: 'right',
        render: (r) => (r.days_to_end === null ? '—' : kind === 'overdue' ? <Badge tone="danger">{-r.days_to_end} days</Badge> : <Badge tone="warning">{r.days_to_end} days</Badge>) },
    ] : []),
    { key: 'gross_margin', label: 'Margin / hr', align: 'right', render: (r) => fmtMoney(r.gross_margin) },
  ];

  return (
    <div className="stack">
      <Purpose what="Gives the operations team a daily to-do list of active placements whose end date needs attention: the projected end has already passed, is coming up soon, or was never set." use="Pick a list, click a recruiter or client chip to narrow it, and click a row (if you can edit) to update the projected end date or record the real end." care="A stale projection makes every headcount and forecast number wrong, so clearing this list keeps the other tabs trustworthy." />
      <div className="grid cols-3">
        {KINDS.map((k) => (
          <div key={k.key} style={{ borderRadius: 12, outline: kind === k.key ? '2px solid var(--accent)' : 'none', outlineOffset: 2 }}>
            {summary ? <Stat label={k.label} value={fmtNumber(summary[k.key].count)}
              sub={`${k.sub} · ${fmtMoney(summary[k.key].hourly_margin)}/hr of margin`} onClick={() => setKind(k.key)} />
              : <div className="card" style={{ padding: 20 }}><Skeleton w="40%" /><div style={{ height: 10 }} /><Skeleton h={28} w="50%" /></div>}
          </div>
        ))}
      </div>

      <Card flush title={KINDS.find((k) => k.key === kind).label}
        subtitle={kind === 'overdue' ? 'Active placements past their projected end date. Update the projection or record the real end date.'
          : kind === 'expiring' ? `Active placements projected to end within the next ${days} days.` : 'Active placements with no projected end date.'}
        actions={kind === 'expiring' && <Select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Window"
          options={[7, 14, 30, 60, 90].map((n) => ({ value: n, label: `Next ${n} days` }))} />}>
        {paged.error && <div style={{ padding: 16 }}><Alert>{paged.error}</Alert></div>}
        {data && data.count > 0 && (data.by_recruiter.length > 0 || data.by_client.length > 0) && (
          <div className="toolbar" style={{ alignItems: 'flex-start' }}>
            {[['Recruiters', data.by_recruiter], ['Clients', data.by_client]].map(([label, list]) => (
              <div key={label} className="row" style={{ gap: 6 }}>
                <span className="muted">{label}</span>
                {list.map((x) => <Chip key={x.name} on={filters.search === x.name} onClick={() => setFilter('search', filters.search === x.name ? '' : x.name)}>{x.name} · {x.count}</Chip>)}
              </div>
            ))}
          </div>
        )}
        <div className="toolbar"><SearchBox value={filters.search} onChange={(v) => setFilter('search', v)} placeholder="Search candidate, client or recruiter…" />
          {canEdit && <span className="muted right">Click a row to update it</span>}</div>
        <DataTable columns={columns} paged={paged} rowKey={(r) => r.s_no} noun="placements" onRowClick={canEdit ? ({ days_to_end, ...row }) => setEdit(row) : undefined}
          empty={<div className="empty"><strong>Nothing here</strong>No placements in this list for the current filters.</div>} />
      </Card>
      {edit && <PlacementForm placement={edit} defaultOrg={org} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
